import type { ApiErrorBody, ApiErrorCode } from '@hemmafixa/shared';

/** Error thrown for any non-2xx API response, in the shared error shape. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly fields: ApiErrorBody['error']['fields'];
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
    this.requestId = body.requestId;
  }
}

const NETWORK_ERROR: ApiErrorBody['error'] = {
  code: 'INTERNAL_ERROR',
  message: 'Could not reach Hemmafixa. Check your internet connection and try again.',
};

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Sent as JSON */
  body?: unknown;
}

// A 401 from these means "wrong password" or "no session", not an expired access cookie
const NO_REFRESH = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

/**
 * Calls the API with the session cookie. Tokens are never stored in the browser:
 * the API sets httpOnly cookies and `credentials: 'include'` sends them.
 *
 * When the short-lived access cookie has expired (401), it asks for new cookies once and repeats the request.
 * Requests that fail at the same time share one refresh, because a refresh token works only once.
 */
export function createApiClient(baseUrl: string, fetchImpl: typeof fetch = (...args) => fetch(...args)) {
  let refreshing: Promise<boolean> | null = null;

  const refresh = () => {
    refreshing ??= send('/auth/refresh', { method: 'POST' })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  };

  async function send(path: string, { body, headers, ...init }: RequestOptions) {
    try {
      return await fetchImpl(`${baseUrl}${path}`, {
        ...init,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new ApiError(0, NETWORK_ERROR);
    }
  }

  return async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response = await send(path, options);
    if (response.status === 401 && !NO_REFRESH.has(path) && (await refresh())) {
      response = await send(path, options);
    }

    const text = await response.text();
    let data: unknown;
    try {
      data = text ? JSON.parse(text) : undefined;
    } catch {
      data = undefined;
    }

    if (!response.ok) {
      const error = (data as ApiErrorBody | undefined)?.error;
      throw new ApiError(
        response.status,
        error ?? { code: 'INTERNAL_ERROR', message: 'Unexpected response from the server.' },
      );
    }
    return data as T;
  };
}
